import videojs from 'video.js';

import ModalDialog from '../../components/modal-dialog.js';
import DownloadIcon from '../../assets/images/download-white-24dp.svg';
import { isPresentationId } from '../../util';

import './downloads.css';

const _enabled = new WeakMap();
const _downloads = new WeakMap();
const _modal = new WeakMap();
let localize;

/**
 * The Downloads module.
 * @member {DownloadsPlugin} downloads
 * @memberof MediasitePlayer#
 */

/**
 * Configures a list of downloadable content that is made available to the user via the control bar menu.<br><br>
 * Note: the Downloads button will only be displayed in the control bar menu if {@link DownloadsPlugin#enabled} is set to <code>true</code>, and at least one item has been added to the list of downloads via {@link DownloadsPlugin#add}.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class DownloadsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        const modal = new ModalDialog(player, {
            temporary: false,
            uncloseable: false,
            pauseOnOpen: false,
            description: localize('Downloads'),
            displayAsPanel: true
        });
        _modal.set(this, modal);
        modal.addClass('mediasite-player__downloads-modal');
        modal.addClass('mediasite-player__control-bar-modal');
        _enabled.set(this, false);
        _downloads.set(this, []);

        player.controlbarMenu().addMenuItem(localize('Downloads'), { id: this.name, icon: DownloadIcon, callback: () => this.openModal() });

        player.ready(() => {
            player.layout().addChild(modal);
            this.#updateVisibility();
        });
    }

    /**
     * Opens the Downloads modal dialog.
     * @example
     * player.downloads.openModal();
     */
    openModal() {
        if (isPresentationId(this.player.loader().presentationId) && this.enabled) {
            const modal = _modal.get(this);
            modal.open();
        }
    }

    /**
     * Closes the Downloads modal dialog.
     * @example
     * player.downloads.closeModal();
     */
    closeModal() {
        const modal = _modal.get(this);
        if (modal?.opened()) {
            modal.close();
        }
    }

    #updateModalContent() {
        const modal = _modal.get(this);
        if (modal) {
            const downloads = _downloads.get(this);
            const heading = document.createElement('h2');
            heading.textContent = localize('Downloads');

            const listElement = document.createElement('ul');
            listElement.classList.add('mediasite-player__downloads');
            for (const { text, url } of downloads) {
                const listItem = listElement.appendChild(document.createElement('li'));
                const link = listItem.appendChild(document.createElement('a'));
                link.download = '';
                link.href = url;
                link.textContent = text;
            }


            modal.fillWith([heading, listElement]);
        }
    }

    /**
     * Adds an item to the list of downloads.
     * @param {string} url A path to the item to download.
     * @param {string} text A label for the item's entry in the Downloads control bar menu.
     * @example
     * player.downloads.add('https://mediasite.com', 'Mediasite');
     */
    add(url, text) {
        const downloads = _downloads.get(this);
        _downloads.set(this, [...downloads, { text, url }]);

        this.#updateVisibility();
    }

    /**
     * Removes an item from the list of downloads that was previously added via [add]{@link MediasitePlayer.downloads#add}.
     * @param {string} url The path to the item.
     * @example
     * player.downloads.remove('https://mediasite.com');
     */
    remove(url) {
        const downloads = _downloads.get(this);
        _downloads.set(this, downloads.filter(item => item.url !== url));

        this.#updateVisibility();
    }

    #updateVisibility() {
        // If enabled + downloads available, show toolbar button
        const downloads = _downloads.get(this);

        if (_enabled.get(this) && downloads.length > 0) {
            this.player.controlbarMenu().enableMenuItem(this.name);
        }
        else {
            this.player.controlbarMenu().disableMenuItem(this.name);
            this.closeModal();
        }
        this.#updateModalContent();
    }

    /**
     * A boolean indicating whether or not the Downloads feature is enabled.
     * @type {boolean}
     * @example
     * player.downloads.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.#updateVisibility();
    }

    dispose() {
        if (this.player?.usingPlugin('controlbarMenu')) {
            this.player.controlbarMenu().removeMenuItem(this.name);
        }

        super.dispose();
    }
}

export default DownloadsPlugin;
